import React from "react"

import { LeadNotesSection } from "../notes/LeadNotesSection.tsx"
import { type LeadWorkspaceTabContext } from "../lead-workspace-tabs.tsx"
import { LeadOverviewActivityCard } from "./LeadOverviewActivityCard.tsx"
import { LeadOverviewCustomerCard } from "./LeadOverviewCustomerCard.tsx"
import { LeadOverviewEmailTemplatesCard } from "./LeadOverviewEmailTemplatesCard.tsx"
import { LeadOverviewFilesCard } from "./LeadOverviewFilesCard.tsx"
import { LeadOverviewFinancialCard } from "./LeadOverviewFinancialCard.tsx"
import { LeadOverviewLocationCard } from "./LeadOverviewLocationCard.tsx"
import { LeadOverviewNeedsInfoSection } from "./LeadOverviewNeedsInfoSection.tsx"
import { LeadOverviewProjectCard } from "./LeadOverviewProjectCard.tsx"

/**
 * A dense CRM-dashboard grid matching the client's mockup row for row: two
 * equal-height cards up top, a ~44/56 split, then Notes/Files at 2/3 : 1/3
 * with Activity dropped to its own full-width row below (2026-09-12) — a
 * Lead's notes carry customer requirements, pricing and next steps, and the
 * former 40/20/40 Activity/Notes/Files row left Notes too narrow to be
 * useful. Lifecycle
 * & Follow-Up (stage, operational state, owner, follow-up, appointment)
 * moved out entirely once the persistent header became their single UI
 * entry point (2026-08-30) — see `LeadWorkspaceHeader.tsx`; the deep-linked
 * "missing field" mark that used to land on that card now lands on the
 * header's own appointment control instead (`LEAD_HEADER_APPOINTMENT_TARGET`
 * in `missing/lead-missing-fields.ts`), the same way the owner control
 * already worked. Proposals & Contracts moved out to its own tab (it had no
 * slot in the mockup and reads as a workspace of its own) — see
 * `documents/LeadDocumentsTab.tsx`. Every card still owns only the fields it
 * writes — this only changes where they sit on the page.
 */
function LeadOverviewTab({
  lead,
  canEdit,
  saving,
  filterOptions,
  onPatch,
  onTabChange
}: LeadWorkspaceTabContext) {
  return (
    <div
      className={"flex flex-col gap-4"}
      data-testid={"lead-overview"}
      data-guide-target="lead-overview-panel"
    >
      <LeadOverviewNeedsInfoSection lead={lead} canEdit={canEdit} />

      {/* items-start, not items-stretch: the Customer summary is
          deliberately compact (~250-280px) and must not be stretched to
          Project's height, per the 2026-08-30 Customer card pass. */}
      <div className={"grid grid-cols-1 lg:grid-cols-2 gap-4 items-start"}>
        <LeadOverviewCustomerCard
          lead={lead}
          filterOptions={filterOptions}
          canEdit={canEdit}
          saving={saving}
          onPatch={onPatch}
        />
        <LeadOverviewProjectCard
          lead={lead}
          filterOptions={filterOptions}
          canEdit={canEdit}
          onPatch={onPatch}
        />
      </div>

      <div className={"grid grid-cols-1 lg:grid-cols-9 gap-4 items-stretch"}>
        <div className={"lg:col-span-4 min-w-0"}>
          <LeadOverviewFinancialCard
            lead={lead}
            canEdit={canEdit}
            onPatch={onPatch}
          />
        </div>
        <div className={"lg:col-span-5 min-w-0"}>
          <LeadOverviewLocationCard
            lead={lead}
            filterOptions={filterOptions}
            canEdit={canEdit}
            onPatch={onPatch}
          />
        </div>
      </div>

      {/* Notes needs real room to be useful for a Lead (customer
          requirements, pricing discussions, next steps) — a 2/3 : 1/3 split
          against Files, with Activity dropping to its own full-width row
          below rather than competing for the same row's height. */}
      <div className={"grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch"}>
        <div className={"lg:col-span-2 min-w-0"}>
          <LeadNotesSection lead={lead} canEdit={canEdit} className={"h-full"} />
        </div>
        <div className={"lg:col-span-1 min-w-0"}>
          <LeadOverviewFilesCard
            attachments={lead?.attachments}
            canEdit={canEdit}
            saving={saving}
            onPatch={onPatch}
            onViewAll={() => onTabChange("files")}
          />
        </div>
      </div>

      <LeadOverviewEmailTemplatesCard
        leadId={lead?.id}
        recipient={(
          lead?.customer?.email ||
          lead?.temporary_customer?.email ||
          ""
        ).trim()}
        canSend={canEdit}
      />

      <LeadOverviewActivityCard
        lead={lead}
        onViewAll={() => onTabChange("activity")}
      />
    </div>
  )
}

export { LeadOverviewTab }
